import { Check, FolderTree, ShieldCheck, Sparkles } from "lucide-react";
import { Card, SectionLabel } from "@omnicanvas/ui";

interface ScreenshotPreviewProps {
  src: string;
  width: number;
  height: number;
  alt: string;
}

/** A real app screenshot, captured from demo data with the app's own components. */
function ScreenshotPreview({ src, width, height, alt }: ScreenshotPreviewProps) {
  return (
    <Card className="overflow-hidden bg-white p-2 shadow-[0_24px_60px_-32px_rgba(15,23,42,0.35)]">
      <img
        src={src}
        width={width}
        height={height}
        loading="lazy"
        decoding="async"
        alt={alt}
        className="h-auto w-full rounded-lg"
      />
    </Card>
  );
}

const outcomes: Array<{
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
  preview: JSX.Element;
  wide?: boolean;
}> = [
  {
    eyebrow: "Capture",
    title: "Start in the shape your thinking actually takes.",
    description:
      "Write, draw, paste images, place sticky notes, and connect ideas without forcing them into a document outline first.",
    bullets: [
      "Infinite canvas with rich text and drawing tools",
      "Templates for meetings, planning, and research",
      "Web clips, PDFs, images, and calendar-aware text",
    ],
    preview: (
      <ScreenshotPreview
        src="/images/capture-canvas.webp"
        width={1600}
        height={1000}
        alt="A launch ideas board: text blocks on positioning and launch week, colored sticky notes, a hand-drawn Capture, Understand, Act sketch, and arrows connecting them."
      />
    ),
  },
  {
    eyebrow: "Connect",
    title: "See how your ideas connect while you write.",
    description:
      "Turn on Concepts and OmniCanvas highlights the ideas that recur across your text blocks and sticky notes, then draws a line between them in that concept's color. Download a small model once and it matches by meaning too, so “revenue” in one block links to “sales” in another.",
    bullets: [
      "Lines update as you type, one color per concept",
      "Matches repeated lines, shared words, and related meanings",
      "Runs on your device; hide any concept with one click",
    ],
    preview: (
      <ScreenshotPreview
        src="/images/concepts-meeting.webp"
        width={1600}
        height={1124}
        alt="Concepts turned on for a launch planning note while hovering a sticky note: onboarding, beta feedback, and pricing tiers are highlighted in their colors, with a labelled line of each color joining the blocks that mention it."
      />
    ),
    // A wide, sparse screenshot: at half width its text is unreadable.
    wide: true,
  },
  {
    eyebrow: "Understand",
    title: "Turn a conversation into work you can act on.",
    description:
      "Record microphone and system audio without adding a bot to the call. Keep the transcript beside decisions, questions, and follow-ups on the same canvas.",
    bullets: [
      "Live transcription and searchable recordings",
      "Structured recaps, decisions, and action items",
      "Transcript Q&A and reusable prompt workflows",
    ],
    preview: (
      <ScreenshotPreview
        src="/images/understand-meeting.webp"
        width={1600}
        height={1302}
        alt="A customer discovery call transcript beside its meeting capture: executive recap, decisions, action items with owners as checkboxes, open questions, and clickable source timestamps."
      />
    ),
  },
  {
    eyebrow: "Act",
    title: "Close the loop before the note disappears into storage.",
    description:
      "Confirm the actions, preserve open questions, and write one sentence that makes the note useful to future you. A weekly Review queue brings edited work back at the right time.",
    bullets: [
      "Closeout prompts on ordinary notes and meeting captures",
      "Actionable Notes template for consistent structure",
      "Review, archive, and cleanup workflow built into the library",
    ],
    preview: (
      <ScreenshotPreview
        src="/images/act-closeout.webp"
        width={1600}
        height={738}
        alt="A beta go/no-go review with its transcript, a capture whose action items are checked off and marked Loop closed, and a What we learned block with a one-sentence takeaway."
      />
    ),
  },
  {
    eyebrow: "Retrieve",
    title: "Find the thread long after the moment has passed.",
    description:
      "Search exact words or related ideas, follow wiki-links, and move between grid, table, Kanban, and graph views as your library grows.",
    bullets: [
      "Full-text and semantic search across notes",
      "Folders, tags, backlinks, and graph view",
      "Offline-first storage with sync and version history",
    ],
    preview: (
      <ScreenshotPreview
        src="/images/retrieve-search.webp"
        width={1600}
        height={1000}
        alt="Library search for pricing: matching tags and notes in the search dropdown, and every matching note across folders shown in the library grid."
      />
    ),
  },
];

export function Features() {
  return (
    <section id="features" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mx-auto mb-20 max-w-3xl text-center">
          <SectionLabel className="mb-4 text-indigo-600">A system, not another app hunt</SectionLabel>
          <h2 className="text-4xl font-extrabold tracking-[-0.035em] text-slate-950 md:text-5xl">
            The app is only one part. The loop makes notes useful.
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-600">
            Capture in the shape that feels natural, add enough structure to
            understand it, decide what happens next, and review it before the
            context goes stale. AI helps with the work; it does not replace the system.
          </p>
        </div>

        <div className="space-y-28">
          {outcomes.map((outcome, index) => (
            <div
              key={outcome.eyebrow}
              className={outcome.wide ? "grid gap-10" : "grid items-center gap-12 lg:grid-cols-2 lg:gap-16"}
            >
              <div className={outcome.wide ? "mx-auto max-w-3xl text-center" : index % 2 ? "lg:order-2" : ""}>
                <SectionLabel className="mb-4 text-indigo-600">{outcome.eyebrow}</SectionLabel>
                <h3 className="text-3xl font-extrabold tracking-tight text-slate-950 md:text-4xl">
                  {outcome.title}
                </h3>
                <p className="mt-5 text-base leading-7 text-slate-600">{outcome.description}</p>
                <ul className={outcome.wide ? "mx-auto mt-7 w-fit space-y-3 text-left" : "mt-7 space-y-3"}>
                  {outcome.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-3 text-sm font-medium text-slate-700">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                        <Check size={12} />
                      </span>
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={!outcome.wide && index % 2 ? "lg:order-1" : ""}>{outcome.preview}</div>
            </div>
          ))}
        </div>

        <Card tone="muted" className="mt-28 grid gap-6 p-6 sm:grid-cols-3 sm:p-8">
          <div className="flex gap-3">
            <ShieldCheck size={20} className="shrink-0 text-emerald-600" />
            <div>
              <div className="text-sm font-bold text-slate-900">Your data stays yours</div>
              <p className="mt-1 text-xs leading-5 text-slate-500">Optional end-to-end encryption and complete exports.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <FolderTree size={20} className="shrink-0 text-indigo-600" />
            <div>
              <div className="text-sm font-bold text-slate-900">Grows without a rebuild</div>
              <p className="mt-1 text-xs leading-5 text-slate-500">Folders, tags, views, and links scale with your library.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Sparkles size={20} className="shrink-0 text-amber-600" />
            <div>
              <div className="text-sm font-bold text-slate-900">AI remains optional</div>
              <p className="mt-1 text-xs leading-5 text-slate-500">Core capture and organization work without an AI workflow.</p>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}
